/**
 * @fileoverview WEB-703 — the ten-slug **event lexicon**: the words an event
 * page uses, and the order its module tier renders in.
 *
 * The central move of the event-detail-v2 design is that a category slug picks
 * a *lexicon* separately from the modules it renders. "About this show" is
 * wrong for a pottery exhibition and "Lineup" is wrong for a seven-course
 * dinner, so the wording is data, not `if (slug === …)` branches scattered
 * through the template. Every surface that needs a heading, a panel kicker, a
 * CTA verb, a breadcrumb label, or the module order reads it from here.
 *
 * What this module owns: **copy and ordering only.**
 * - Colors are WEB-701 (`utils/categoryColors.ts` → `getCategoryChroma`).
 * - Gating — whether a module has anything to show — is WEB-700
 *   (`lib/eventDetail/modules.ts` → `resolveEventModules`).
 * - Module *renderers* are M2 (WEB-714 onward); they consume the wording here
 *   and must never re-derive it.
 *
 * The map is closed against the canonical ten by
 * `satisfies Record<CategorySlug, EventLexiconEntry>` — deleting a slug entry,
 * or inventing an eleventh, fails `pnpm --filter web type-check`. The ten
 * themselves come from `config/categories.ts` (WEB-606); they are deliberately
 * not re-declared here.
 *
 * No proper nouns. This file carries category vocabulary, never a venue,
 * franchise, artist, or person name — `__tests__/eventLexicon.test.ts` asserts
 * that against a denylist, which also keeps the no-realistic-fake-data policy
 * intact for a file that is pure copy.
 */
import type {
  EventLexiconModuleEntry,
  EventModuleKind,
  EventModuleLexicon,
} from '@/lib/eventDetail/modules';

import { CATEGORY_LABELS, type CategorySlug } from './categories';

/**
 * Section-meta string EventModuleSection paints when `hasContent` is false
 * (WEB-719 / WEB-805). One phrase, every kind that shows one — the page's
 * shape stays the same even when the body is a degrade note rather than rows.
 * Describes LOCI's listing, never a third party's publishing state.
 */
export const EVENT_MODULE_NOT_PUBLISHED_META = 'Not listed';

/**
 * Per-kind resolution of that meta, `null` meaning "paint no meta at all"
 * (WEB-851).
 *
 * `seatTiers` opts out. Its section is the page's ticketing block, and a
 * right-aligned "NOT LISTED" over a body that already says, in a full
 * sentence, what LOCI does not hold is the same absence stated twice — the
 * second time in a register terse enough to read as a claim about the ticket
 * itself rather than about our record of it. The degrade paragraph is the
 * honest, unambiguous statement; the caps label adds nothing to it.
 *
 * This is a per-KIND decision and lives here, beside the degrade copy it
 * pairs with, so `EventModuleSection` keeps its documented freedom from
 * kind-specific branching. `satisfies Record<EventModuleKind, …>` is the
 * compile-time completeness gate: a new kind must state its answer rather
 * than inherit one.
 */
const EVENT_MODULE_NOT_PUBLISHED_META_BY_KIND = {
  about: EVENT_MODULE_NOT_PUBLISHED_META,
  lineup: EVENT_MODULE_NOT_PUBLISHED_META,
  matchup: EVENT_MODULE_NOT_PUBLISHED_META,
  seatTiers: null,
  works: EVENT_MODULE_NOT_PUBLISHED_META,
  runOfDates: EVENT_MODULE_NOT_PUBLISHED_META,
  menu: EVENT_MODULE_NOT_PUBLISHED_META,
  agenda: EVENT_MODULE_NOT_PUBLISHED_META,
  people: EVENT_MODULE_NOT_PUBLISHED_META,
  audience: EVENT_MODULE_NOT_PUBLISHED_META,
  generic: EVENT_MODULE_NOT_PUBLISHED_META,
} as const satisfies Record<EventModuleKind, string | null>;

/**
 * The section meta for a degraded module of this kind, or `null` when the
 * kind paints none.
 */
export function getEventModuleNotPublishedMeta(kind: EventModuleKind): string | null {
  return EVENT_MODULE_NOT_PUBLISHED_META_BY_KIND[kind];
}

/**
 * Per-kind degrade copy (WEB-719 / WEB-805 / WEB-1188). One short sentence
 * each, present tense. Never a claim about what a venue, league, kitchen, or
 * organizer has published or will post. Never "No data available", never
 * "N/A" / "TBD" / an em-dash placeholder. The section chrome looks this up
 * by `EventModuleKind` so no individual module implements the note.
 *
 * Lineup is the one kind that can add a second sentence, and only when the
 * event has a usable ticket link (`lineupDegradeCopy`). The map itself stays
 * the base sentence so a lookup without that flag does not invent a ticket
 * page.
 *
 * `satisfies Record<EventModuleKind, string>` is the compile-time
 * completeness gate; `__tests__/eventLexicon.test.ts` iterates the union
 * at runtime and fails on a missing or empty entry.
 */
export const LINEUP_DEGRADE_COPY = 'Lineup not listed.';
export const LINEUP_TICKET_BILL_COPY = 'The ticket page has the full bill.';

export function lineupDegradeCopy(hasTicketLink: boolean): string {
  if (!hasTicketLink) return LINEUP_DEGRADE_COPY;
  return `${LINEUP_DEGRADE_COPY} ${LINEUP_TICKET_BILL_COPY}`;
}

export const EVENT_MODULE_DEGRADE_COPY = {
  about: 'Details not listed.',
  lineup: LINEUP_DEGRADE_COPY,
  matchup: 'Matchup not listed.',
  // WEB-851: the module is the ticketing block, not a seat-tier list — it
  // prints the price story, the ticket limit, and the seat map when we hold
  // them, and this note only when we hold none of them. The prior copy named
  // "seat tiers or prices" specifically, which read as a claim that no price
  // existed even on events whose price we DO publish two inches above, under
  // the CTA. The residual is "No more ticket details listed."
  seatTiers: 'No more ticket details listed.',
  works: 'Works not listed.',
  runOfDates: 'Dates not listed.',
  menu: 'Menu not listed.',
  agenda: 'Agenda not listed.',
  people: 'Lineup not listed.',
  audience: 'Not listed.',
  generic: 'Not listed.',
} as const satisfies Record<EventModuleKind, string>;

export interface EventModuleDegradeCopyOptions {
  readonly hasTicketLink?: boolean;
}

/** Resolves the WEB-719 degrade paragraph for one module kind. */
export function getEventModuleDegradeCopy(
  kind: EventModuleKind,
  options: EventModuleDegradeCopyOptions = {},
): string {
  if (kind === 'lineup' && options.hasTicketLink === true) {
    return lineupDegradeCopy(true);
  }
  return EVENT_MODULE_DEGRADE_COPY[kind];
}

/**
 * Everything the page says for one category slug.
 *
 * Extends — rather than restates — the module-tier shape `resolveEventModules`
 * reads, so the selector accepts a lexicon entry directly and the ordered
 * module set cannot drift into two representations.
 */
export interface EventLexiconEntry extends EventModuleLexicon {
  /** Editorial-column About heading: 'About this show' | 'About this exhibition' | … */
  aboutHeading: string;
  /**
   * Kicker above the venue line. **Spatial rule (WEB-757):** `'Venue'` when
   * the event happens in a room (walls); `'Place'` only where there is no
   * room to be in — a street corridor (`community-culture`) or a greenway
   * (`wellness`). That is the design-source rule and the one a reader can
   * verify from the page itself. It supersedes the WEB-703 ticketing rule
   * ("Venue for ticketed rooms, Place for spaces you drop into"), which
   * labelled restaurants, museums, bars, and workshops as Place.
   */
  venueKicker: string;
  /** Decision-panel kicker for the start-time row: 'Doors' | 'Gates' | 'Seating' | 'Check-in' | 'Opens'. */
  panelKicker: string;
  /** Primary decision-panel CTA verb. */
  ctaLabel: string;
  /** Category segment of the `CLT › DISCOVER › <CATEGORY> › <VENUE>` trail. */
  breadcrumbCategoryLabel: string;
}

/**
 * The four generic slugs (`drinks`, `business-networking`, `wellness`,
 * `hobbies-interests`) run one module, not a bespoke set — the design's most
 * important restraint: a category earns a bespoke module when its data model is
 * genuinely different, not merely because it is a different category. They
 * share this order and differ only in the heading, which is why WEB-714 can
 * ship a single `GenericModule` renderer for all four (it registers `generic`
 * and `agenda` to the same renderer, so a slug whose list is time-led renders
 * through the same component).
 */
function genericModuleOrder(title: string): readonly EventLexiconModuleEntry[] {
  return [{ kind: 'generic', title }];
}

/**
 * WEB-855 — the one heading the organizer slot uses, everywhere.
 *
 * Three lexicons used to word this slot per category ('Hosts' for food,
 * 'Speakers' for science-tech, 'Organizers' for community-culture) on the
 * theory that the roster meant something different in each. It does not: the
 * only row source that has ever produced content is `event.organizer`, the
 * host the Eventbrite `/e/` page publishes, and a dinner's host and a
 * conference's host are the same fact. 'Speakers' over a single organizer row
 * was the worst of the three — it named a role the row does not hold.
 *
 * The second, still-inert source (non-performing contributors, awaiting a
 * `billing` contract that can express one) is the reason this is 'Organized
 * by' rather than 'Organizer': the phrase reads correctly over one row or
 * several, and over a mixed roster, without needing to know the count.
 */
const ORGANIZER_MODULE_TITLE = 'Organized by';

/**
 * FRE-1313 / WEB-855 — every category leads with its About column and closes
 * with the organizer slot; the middle is what makes the category itself.
 *
 * Returning BOTH fields from one call is the point: `aboutHeading` had
 * exactly one definition per slug before this module existed, and writing it
 * a second time inside `moduleOrder` is how the two would drift. It was dead
 * config until FRE-1313 — declared for all eleven entries and read by
 * nothing, because the column it was written for was never built.
 *
 * **About is first for every slug** without exception: the body copy is what
 * a reader wants before a lineup, a menu, or a seat tier, and a per-category
 * override would be a position with no reason behind it.
 *
 * **The organizer slot is last for every slug**, and stating it here rather
 * than in eleven entries is what makes that true by construction. WEB-855
 * needed it on all ten plus the fallback — WEB-854 made
 * `EventDetailV2.organizer` real for every Eventbrite event, but only three
 * lexicons ordered a `people` module, so on the other seven the host was
 * fetched, stored, served, and rendered nowhere a reader could see it. Adding
 * it per entry would have re-opened the same drift for the eighth category
 * someone adds.
 *
 * Last, specifically, because attribution closes a section rather than opens
 * one: a reader wants what the event IS (about), then what is in it (lineup,
 * menu, works, agenda), then who put it on. The three lexicons that already
 * ordered `people` had it last in two of three, so this also costs the least
 * movement. Suppression for sources that never publish an organizer is NOT
 * decided here — that is `isModuleSlotApplicable` in `lib/eventDetail/
 * modules.ts`, because it depends on the event, and this file is copy and
 * ordering only.
 */
function aboutLedOrganizerClosed(
  aboutHeading: string,
  middle: readonly EventLexiconModuleEntry[] = [],
): Pick<EventLexiconEntry, 'aboutHeading' | 'moduleOrder'> {
  return {
    aboutHeading,
    moduleOrder: [
      { kind: 'about', title: aboutHeading },
      ...middle,
      { kind: 'people', title: ORGANIZER_MODULE_TITLE },
    ],
  };
}

/**
 * The lexicon for the canonical ten.
 *
 * `satisfies Record<CategorySlug, …>` is the compile-time completeness gate;
 * `__tests__/eventLexicon.test.ts` covers what the type cannot see (key parity
 * with `CATEGORY_ORDER`, module-kind membership, the proper-noun denylist).
 */
export const EVENT_LEXICON = {
  music: {
    venueKicker: 'Venue',
    panelKicker: 'Doors',
    ctaLabel: 'Get tickets',
    breadcrumbCategoryLabel: CATEGORY_LABELS.music,
    ...aboutLedOrganizerClosed('About this show', [
      { kind: 'lineup', title: 'Lineup' },
      { kind: 'seatTiers', title: 'Tickets' },
    ]),
  },
  sports: {
    venueKicker: 'Venue',
    panelKicker: 'Gates',
    ctaLabel: 'Find seats',
    breadcrumbCategoryLabel: CATEGORY_LABELS.sports,
    ...aboutLedOrganizerClosed('About this game', [
      { kind: 'matchup', title: 'Matchup' },
      // WEB-851 — same heading as music, deliberately. Every other decision
      // this ticket made about `seatTiers` is keyed on the KIND, not the
      // category: the degrade copy, the suppressed section meta, and the
      // price-story body. A section still headed "Seats" would carry all
      // three and say nothing about seats — "No more ticket details listed."
      // under a seat heading is the same mislabelling
      // WEB-805's per-kind copy map exists to prevent. The design's
      // category-word system (§2.5) named this slot for a per-tier seating
      // list that has never had a backing field; what the slot actually
      // renders is ticketing.
      { kind: 'seatTiers', title: 'Tickets' },
    ]),
  },
  food: {
    venueKicker: 'Venue',
    panelKicker: 'Seating',
    ctaLabel: 'Book a seat',
    breadcrumbCategoryLabel: CATEGORY_LABELS.food,
    ...aboutLedOrganizerClosed('About this dinner', [{ kind: 'menu', title: 'Menu' }]),
  },
  'science-tech': {
    venueKicker: 'Venue',
    panelKicker: 'Check-in',
    ctaLabel: 'Get tickets',
    breadcrumbCategoryLabel: CATEGORY_LABELS['science-tech'],
    ...aboutLedOrganizerClosed('About this session', [{ kind: 'agenda', title: 'Agenda' }]),
  },
  art: {
    venueKicker: 'Venue',
    panelKicker: 'Opens',
    ctaLabel: 'Add to your week',
    breadcrumbCategoryLabel: CATEGORY_LABELS.art,
    ...aboutLedOrganizerClosed('About this exhibition', [
      { kind: 'works', title: 'Works' },
      { kind: 'runOfDates', title: 'Run of dates' },
    ]),
  },
  drinks: {
    venueKicker: 'Venue',
    panelKicker: 'Doors',
    ctaLabel: 'Add to your week',
    breadcrumbCategoryLabel: CATEGORY_LABELS.drinks,
    ...aboutLedOrganizerClosed('About this tasting', genericModuleOrder('Pour list')),
  },
  'business-networking': {
    venueKicker: 'Venue',
    panelKicker: 'Check-in',
    ctaLabel: 'Get tickets',
    breadcrumbCategoryLabel: CATEGORY_LABELS['business-networking'],
    ...aboutLedOrganizerClosed('About this session', genericModuleOrder('Agenda')),
  },
  'community-culture': {
    venueKicker: 'Place',
    panelKicker: 'Opens',
    ctaLabel: 'Add to your week',
    breadcrumbCategoryLabel: CATEGORY_LABELS['community-culture'],
    ...aboutLedOrganizerClosed('About this gathering', [
      { kind: 'audience', title: "Who it's for" },
    ]),
  },
  wellness: {
    venueKicker: 'Place',
    panelKicker: 'Check-in',
    ctaLabel: 'Book a seat',
    breadcrumbCategoryLabel: CATEGORY_LABELS.wellness,
    ...aboutLedOrganizerClosed('About this class', genericModuleOrder('What to bring')),
  },
  'hobbies-interests': {
    venueKicker: 'Venue',
    panelKicker: 'Check-in',
    ctaLabel: 'Book a seat',
    breadcrumbCategoryLabel: CATEGORY_LABELS['hobbies-interests'],
    ...aboutLedOrganizerClosed('About this workshop', genericModuleOrder('Skill level')),
  },
} as const satisfies Record<CategorySlug, EventLexiconEntry>;

/**
 * Fallback for a slug outside the canonical ten.
 *
 * An event whose category the taxonomy does not recognise still has to render
 * a complete page, so the resolver degrades to neutral vocabulary rather than
 * throwing or handing the template `undefined`. Deliberately not one of the
 * ten — borrowing `music`'s words would tell the reader something false about
 * an event we cannot classify.
 */
export const GENERIC_EVENT_LEXICON: EventLexiconEntry = {
  venueKicker: 'Venue',
  panelKicker: 'Doors',
  ctaLabel: 'Add to your week',
  breadcrumbCategoryLabel: 'Events',
  ...aboutLedOrganizerClosed('About this event', genericModuleOrder('Details')),
};

/**
 * Resolves a category slug's lexicon, falling back to
 * {@link GENERIC_EVENT_LEXICON} for anything outside the canonical ten.
 *
 * Takes a `string` on purpose: callers hold a slug off the wire or out of a
 * URL segment, not a `CategorySlug`, and the whole point of the fallback is
 * that an unrecognised value is a normal outcome. `Object.hasOwn` keeps
 * inherited `Object.prototype` keys (`'toString'`, `'constructor'`) from
 * resolving to a function instead of a lexicon entry.
 */
export function getEventLexicon(slug: string): EventLexiconEntry {
  return Object.hasOwn(EVENT_LEXICON, slug)
    ? EVENT_LEXICON[slug as CategorySlug]
    : GENERIC_EVENT_LEXICON;
}
